import React, { useEffect, useMemo, useState } from 'react';
import {
  NodeCategoryGroup,
  DETAIL_VIEW_TABLE_COLS,
  EDetailViewCols,
  DetailViewSortColumn,
  SortDirection,
  TreeNodesContainerProps,
  EDetailViewFilterTypes,
  DetailViewTableColProps,
} from 'components/main/tree/constants';
import {
  DateRangeFilter,
  FilterMap,
  filterNodes,
  findNewestByDate,
  findOldestByDate,
  sortNodes,
} from 'components/main/tree/utils';
import {
  FmlxTableBody,
  FmlxTableContainer,
  FmlxTableGroupRows,
  FmlxTableHead,
  FmlxTableHeadCell,
  FmlxTableRow,
  FmlxTextBox,
} from 'fmlx-common-ui';
import ETreeGroupTypes from 'enums/ETreeGroupTypes';
import { TreeNodeTypeNames } from 'enums/ETreeNodeTypes';
import NodeDetailViewRow from 'components/customComponents/TreeControl/NodeDetailView/NodeDetailViewRow';
import './NodeDetailView.scss';
import { NodeDetailViewDateFilter } from './NodeDetailViewDateFilter';

const NodeDetailView = (props: TreeNodesContainerProps) => {
  const { treeNodes, groupBy, selectedNodes, selectedExplorerNode, nodeClickHandler } =
    props;
  const [sortColumn, setSortColumn] = useState<DetailViewSortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [filterMap, setFilterMap] = useState<FilterMap[]>([]);
  const [dateRanges, setDateRanges] = useState<
    Record<string, { start?: string; end?: string }>
  >({});

  const handleSort = (column: DetailViewSortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const processedData = useMemo<NodeCategoryGroup[]>(() => {
    return Object.keys(treeNodes).map((key) => ({
      category: TreeNodeTypeNames[treeNodes[key]?.category] || 'Items',
      nodes: (treeNodes[key]?.data || []).map((node) => ({
        ...node,
        dateDispensed: findOldestByDate(
          node?.extra?.plates,
          EDetailViewCols.DateDispensed,
        ),
        lastImaged: findNewestByDate(node?.extra?.plates, EDetailViewCols.LastImaged),
      })),
    }));
  }, [treeNodes]);

  const filteredData = useMemo<NodeCategoryGroup[]>(() => {
    return processedData.map((group) => ({
      ...group,
      nodes: filterNodes(group.nodes, filterMap),
    }));
  }, [processedData, filterMap]);

  const sortedData = useMemo<NodeCategoryGroup[]>(() => {
    return filteredData.map((group) => ({
      ...group,
      nodes: sortNodes(group.nodes, sortColumn, sortDirection),
    }));
  }, [filteredData, sortColumn, sortDirection]);

  const totalNodes = useMemo(() => {
    return sortedData.reduce((sum, group) => sum + group.nodes.length, 0);
  }, [sortedData]);

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    return () => {
      setCollapsedGroups(new Set());
      setFilterMap([]);
      setDateRanges({});
    };
  }, [treeNodes]);

  const onFilterChange = (
    columnKey: string,
    filterType: EDetailViewFilterTypes,
    value: any,
  ) => {
    const newFilter: FilterMap = {
      column: columnKey,
      filterType: filterType,
      value: value,
    };

    setFilterMap((prev) => {
      const otherFilters = prev.filter((f) => f.column !== columnKey);
      // remove this column's filter if there's no value
      if (EDetailViewFilterTypes.Text && !value) {
        return otherFilters;
      }
      return [...otherFilters, newFilter];
    });
  };

  const onDateFilterChange = (start, end, columnKey: string) => {
    const value: DateRangeFilter = {
      startDate: start,
      endDate: end,
    };
    onFilterChange(columnKey, EDetailViewFilterTypes.DateRange, value);
  };

  const stopFilterContextMenuPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleFilterPaste = (
    columnKey: string,
    filterType: EDetailViewFilterTypes,
    e: React.ClipboardEvent,
  ) => {
    e.stopPropagation();
    const input = e.target as HTMLInputElement | null;
    if (input?.tagName === 'INPUT') {
      requestAnimationFrame(() => {
        onFilterChange(columnKey, filterType, input.value);
      });
    }
  };

  const filterRenderers: Record<
    EDetailViewFilterTypes,
    (column: DetailViewTableColProps) => React.ReactNode
  > = {
    [EDetailViewFilterTypes.DateRange]: (column) => (
      <div className="pt-2" onContextMenu={stopFilterContextMenuPropagation}>
        <NodeDetailViewDateFilter
          className="node-detail-view-date-filter"
          startDate={dateRanges[column.key]?.start}
          endDate={dateRanges[column.key]?.end}
          onDateRangeChange={(start, end) => {
            setDateRanges((prev) => ({
              ...prev,
              [column.key]: { start: start, end: end },
            }));
            onDateFilterChange(start, end, column.key);
          }}
        />
      </div>
    ),
    [EDetailViewFilterTypes.Text]: (column) => (
      <div
        className="pt-2 node-filter-reduced-margin"
        onContextMenu={stopFilterContextMenuPropagation}
        onPasteCapture={(e) => handleFilterPaste(column.key, column.filterType, e)}
      >
        <FmlxTextBox
          value={filterMap.find((f) => f.column === column.key)?.value || ''}
          variant={FmlxTextBox.Variant.OUTLINE}
          mode={FmlxTextBox.Mode.TEXT}
          size={FmlxTextBox.Size.SMALL}
          onChange={({ value }) => onFilterChange(column.key, column.filterType, value)}
        />
      </div>
    ),
    [EDetailViewFilterTypes.None]: () => null, // Nothing to render
  };

  return (
    <FmlxTableContainer
      data-testid="node-detail-view-table"
      classNames={{
        base: 'detail-view-table',
        content: '',
      }}
      sxs={{
        base: {
          height: 'calc(100vh - 180px)',
        },
      }}
      stickyHeader
    >
      <FmlxTableHead data-testid="node-detail-view-table-header">
        <FmlxTableRow
          id="node-detail-view-table-header-row"
          onClick={() => {}}
          data-testid="node-detail-view-table-header-row"
        >
          {DETAIL_VIEW_TABLE_COLS.columns.map((column) => (
            <FmlxTableHeadCell
              data-testid={`node-detail-view-table-header-cell-${column.key}`}
              id={column.key}
              key={column.key}
              onClickSort={() => handleSort(column.key as DetailViewSortColumn)}
              align={column.align}
              label={column.label}
              className={column.className}
              sortDisabled={!column.sortable}
              sortDirection={sortColumn === column.key ? sortDirection : null}
              variant={'head'}
              size={'small'}
            >
              {column.filterType && filterRenderers[column.filterType]?.(column)}
            </FmlxTableHeadCell>
          ))}
        </FmlxTableRow>
      </FmlxTableHead>
      <FmlxTableBody data-testid="node-detail-view-table-body">
        {totalNodes === 0 ? (
          <FmlxTableRow
            data-testid="node-detail-view-table-row-empty"
            onClick={() => {}}
          />
        ) : (
          sortedData.map((group, groupIndex) => (
            <React.Fragment key={`category-${groupIndex}`}>
              {groupBy !== ETreeGroupTypes.None ? (
                <FmlxTableGroupRows
                  data-testid={`node-detail-view-table-rows-group-${group.category}`}
                  label={`${group.category}s (${group.nodes.length})`}
                  colSpan={DETAIL_VIEW_TABLE_COLS.columns.length}
                  onClick={() => toggleGroup(group.category)}
                  open={!collapsedGroups.has(group.category)}
                >
                  {group.nodes.map((node) => (
                    <NodeDetailViewRow
                      data-testid={`node-detail-view-table-group-row-node-${node.id}`}
                      key={`node-${node.id}`}
                      node={node}
                      nodeClickHandler={nodeClickHandler}
                      selectedNodes={selectedNodes}
                      selectedExplorerNode={selectedExplorerNode}
                    />
                  ))}
                </FmlxTableGroupRows>
              ) : (
                group.nodes.map((node) => (
                  <NodeDetailViewRow
                    data-testid={`node-detail-view-table-row-node-${node.id}`}
                    key={`node-${node.id}`}
                    node={node}
                    nodeClickHandler={nodeClickHandler}
                    selectedNodes={selectedNodes}
                    selectedExplorerNode={selectedExplorerNode}
                  />
                ))
              )}
            </React.Fragment>
          ))
        )}
      </FmlxTableBody>
    </FmlxTableContainer>
  );
};

export default NodeDetailView;
